import React, { useState, useEffect, useCallback } from 'react';
import { useMutation } from 'react-query';
import AxiosUtil from '../../Utils/AxiosUtil';
import {
    ManagedActionModal,
    ManagedActionModalMode,
    ManagedActionModalState,
    ManagedApiError,
    buildManagedActionModalState,
} from '../Shared/ManagedHelpers';
import {
    Alert as ServaAlert,
    Button,
    Checkbox,
    Col,
    Form,
    Input,
    Row,
    Space,
    Spin,
    Text,
    Title,
} from '@servahealth/serva-ui-kit';
import Administration from '../../Constants/Endpoints/Administration';
import CreateTenantRequest from '../../Interfaces/Requests/Administration/CreateTenantRequest';
import UpdateTenantRequest from '../../Interfaces/Requests/Administration/UpdateTenantRequest';
import GetTenantAdminModelResponse from '../../Interfaces/Responses/Administration/GetTenantAdminModelResponse';

type TenantProps = {
    id: number;
    name: string;
    connString: string;
    logoImage: string;
    nameGeneric: string;
    hideFromDemo: boolean;
    sqlFirstDayOfWeek: number;
    linkedDbName: string;
    urlBase: string;
    encryptKey: string,
    encryptCert: string,
    newTenant: boolean;
    close: () => void;
    refetch: () => void;
};

const Tenant: React.FC<TenantProps> = ({ ...props }) => {
    const [tenantName, setTenantName] = useState(props.name);
    const [connString, setConnString] = useState(props.connString);
    const [logoImage, setLogoImage] = useState(props.logoImage);
    const [tenantGenericName, setTenantGenericName] = useState(props.nameGeneric);
    const [autoFillTenantGenericName, setAutoFillTenantGenericName] = useState(true);
    const [hideFromDemo, setHideFromDemo] = useState(props.hideFromDemo);
    const [sqlFirstDayOfWeek, setSQLFirstDayOfWeek] = useState(props.sqlFirstDayOfWeek);
    const [linkedDbName, setLinkedDbName] = useState(props.linkedDbName);
    const [urlBase, setURLBase] = useState(props.urlBase);
    const [encryptionKey, setEncryptionKey] = useState(props.encryptKey);
    const [encryptionCert, setEncryptionCert] = useState(props.encryptCert);
    const [submitDisabled, setSubmitDisabled] = useState(true);

    const reinstateValues = useCallback(() => {
        setTenantName(props?.name);
        setConnString(props?.connString);
        setLogoImage(props?.logoImage);
        setTenantGenericName(props?.nameGeneric);
        setAutoFillTenantGenericName(true);
        setHideFromDemo(props?.hideFromDemo);
        setSQLFirstDayOfWeek(props?.sqlFirstDayOfWeek);
        setLinkedDbName(props?.linkedDbName);
        setURLBase(props?.urlBase);
        setEncryptionKey(props?.encryptKey);
        setEncryptionCert(props?.encryptCert);
    }, [
        props?.name,
        props?.connString,
        props?.logoImage,
        props?.nameGeneric,
        props?.hideFromDemo,
        props?.sqlFirstDayOfWeek,
        props?.linkedDbName,
        props?.urlBase,
        props?.encryptKey,
        props?.encryptCert
    ]);

    const [formCreUpdTenant] = Form.useForm();

    const [currentError, setCurrentError] = useState('');

    const [manageActionModalState, setManageActionModalState] = useState<ManagedActionModalState>({
        open: false,
        mode: 'info',
        title: '',
        message: '',
        isError: false,
    });

    const showTenantInfoModal = (
        title: string,
        message: string,
        isError: boolean = true,
        mode: ManagedActionModalMode = 'info'
    ) => {
        setManageActionModalState(buildManagedActionModalState(mode, title, message, isError));
    };

    const getTenantActionModalMode = (): ManagedActionModalMode => {
        return props.newTenant ? 'add' : 'update';
    };

    const handleManageActionModalOk = () => {
        const shouldCloseAfterDismiss = manageActionModalState.open && !manageActionModalState.isError;

        setManageActionModalState((currentState) => ({
            ...currentState,
            open: false,
        }));

        if (shouldCloseAfterDismiss) {
            props.close();
            props.refetch();
        }
    };

    const handleTenantFinishFailed = () => {
        showTenantInfoModal(
            props.newTenant ? 'Create Tenant Error' : 'Update Tenant Error',
            'The Tenant form could not be submitted.  Review the form and try again.',
            true,
            getTenantActionModalMode()
        );
    };

    useEffect(() => {
        reinstateValues();
        formCreUpdTenant.setFieldValue('creUpdTenantName', props?.name);
        formCreUpdTenant.setFieldValue('creUpdConnString', props?.connString);
        formCreUpdTenant.setFieldValue('creUpdLogoImage', props?.logoImage);
        formCreUpdTenant.setFieldValue('creUpdTenantGenericName', props?.nameGeneric);
        formCreUpdTenant.setFieldValue('creUpdSQLFirstDayOfWeek', props?.sqlFirstDayOfWeek);
        formCreUpdTenant.setFieldValue('creUpdLinkedDBName', props?.linkedDbName);
        formCreUpdTenant.setFieldValue('creUpdURLBase', props?.urlBase);
        formCreUpdTenant.setFieldValue('creUpdEncryptionKey', props?.encryptKey);
        formCreUpdTenant.setFieldValue('creUpdEncryptionCert', props?.encryptCert);
    }, [
        reinstateValues,
        formCreUpdTenant,
        props?.name,
        props?.connString,
        props?.logoImage,
        props?.nameGeneric,
        props?.hideFromDemo,
        props?.sqlFirstDayOfWeek,
        props?.linkedDbName,
        props?.urlBase,
        props?.encryptKey,
        props?.encryptCert,
        props?.hideFromDemo
    ]);

    const handleSubmitDisabled = useCallback(() => {
        if (tenantName && connString) {
            setSubmitDisabled(false);
        } else {
            setSubmitDisabled(true);
        }
    }, [tenantName, connString]);

    useEffect(() => {
        handleSubmitDisabled();
    }, [handleSubmitDisabled, tenantName]);

    const handleTenantNameChange = (aValue: React.ChangeEvent<HTMLInputElement>) => {
        setTenantName(aValue.target.value);
        handleSubmitDisabled();
    };
    const handleConnStringChange = (aValue: React.ChangeEvent<HTMLInputElement>) => {
        setConnString(aValue.target.value);
        handleSubmitDisabled();
    };
    const handleLogoImageChange = (aValue: React.ChangeEvent<HTMLInputElement>) => {
        setLogoImage(aValue.target.value);
    };
    const handleTenantGenericNameChange = (aValue: React.ChangeEvent<HTMLInputElement>) => {
        setTenantGenericName(aValue.target.value);
    };
    const handleAutoFillTenantGenericName = (aValue: { target: { checked: boolean } }) => {
        setAutoFillTenantGenericName(aValue.target.checked);
    };
    const handleHideFromDemoChange = (aValue: { target: { checked: boolean } }) => {
        setHideFromDemo(aValue.target.checked);
    };
    const handleSQLFirstDayOfWeekChange = (aValue: React.ChangeEvent<HTMLInputElement>) => {
        setSQLFirstDayOfWeek(Number(aValue.target.value));
    };
    const handleLinkedDbNameChange = (aValue: React.ChangeEvent<HTMLInputElement>) => {
        setLinkedDbName(aValue.target.value);
    };
    const handleURLBaseChange = (aValue: React.ChangeEvent<HTMLInputElement>) => {
        setURLBase(aValue.target.value);
    };
    const handleEncryptionKeyChange = (aValue: React.ChangeEvent<HTMLInputElement>) => {
        setEncryptionKey(aValue.target.value);
    };
    const handleEncryptionCertChange = (aValue: React.ChangeEvent<HTMLInputElement>) => {
        setEncryptionCert(aValue.target.value);
    };

    const onFinishCreUpdTenant = () => {
        if (props.newTenant) {
            creTenant();
        } else {
            updTenant();
        }
    };

    const { mutate: creTenant, isLoading: isCreTenantLoading, isError: isCreTenantError, error: creTenantError, } = useMutation(
        async () => {
            try {
                const url = `${process.env.REACT_APP_API_ROOT}${Administration.CREATE_TENANT_REQUEST}`;
                const requestData: CreateTenantRequest = {
                    name: tenantName,
                    connString: connString,
                    logoImage: logoImage,
                    nameGeneric: tenantGenericName ? tenantGenericName : (autoFillTenantGenericName ? tenantName : ''),
                    hideFromDemo: hideFromDemo,
                    sqlFirstDayOfWeek: sqlFirstDayOfWeek,
                    linkedDbName: linkedDbName,
                    urlBase: urlBase,
                    encryptKey: encryptionKey,
                    encryptCert: encryptionCert
                };
                const responseData = await AxiosUtil.post<GetTenantAdminModelResponse, CreateTenantRequest>(url, requestData);
                return responseData;
            } catch (err) {
                setCurrentError('CREATE_TENANT_REQUEST: [Create Tenant] Internal Error: ' + err);
                console.error('CREATE_TENANT_REQUEST: [Create Tenant] Internal Error: ', err);
                showTenantInfoModal(
                    'Create Tenant Error',
                    `Create Tenant: ${tenantName} Failed. Please Contact Application Support`,
                    true,
                    'add'
                );
                return Promise.reject(err);
            }
        },
        {
            onError: (err: unknown) => {
                setCurrentError('CREATE_TENANT_REQUEST: [Create Tenant] Internal Error: ' + err);
                console.error('CREATE_TENANT_REQUEST: [Create Tenant] Internal Error: ', err);
                showTenantInfoModal(
                    'Create Tenant Error',
                    `Create Tenant: ${tenantName} Failed. Please Contact Application Support`,
                    true,
                    'add'
                );
            },
            onSuccess: (response: GetTenantAdminModelResponse) => {
                showTenantInfoModal(
                    'Create Tenant',
                    `Create Tenant: ${tenantName} Succeeded.`,
                    false,
                    'add'
                );
            },
        }
    );

    const { mutate: updTenant, isLoading: isUpdTenantLoading, isError: isUpdTenantError, error: updTenantError, } = useMutation(
        async () => {
            try {
                const url = `${process.env.REACT_APP_API_ROOT}${Administration.UPD_TENANT_REQUEST}`;
                const requestData: UpdateTenantRequest = {
                    id: props.id,
                    name: tenantName,
                    connString: connString,
                    logoImage: logoImage,
                    nameGeneric: tenantGenericName,
                    hideFromDemo: hideFromDemo,
                    sqlFirstDayOfWeek: sqlFirstDayOfWeek,
                    linkedDbName: linkedDbName,
                    urlBase: urlBase,
                    encryptKey: encryptionKey,
                    encryptCert: encryptionCert
                };
                const responseData = await AxiosUtil.post<GetTenantAdminModelResponse, UpdateTenantRequest>(url, requestData);
                return responseData;
            } catch (err) {
                setCurrentError('UPD_TENANT_REQUEST: [Update Tenant] Internal Error: ' + err);
                console.error('UPD_TENANT_REQUEST: [Update Tenant] Internal Error: ', err);
                showTenantInfoModal(
                    'Update Tenant Error',
                    `Update Tenant: ${tenantName} Failed. Please Contact Application Support`,
                    true,
                    'update'
                );
                return Promise.reject(err);
            }
        },
        {
            onError: (err: unknown) => {
                setCurrentError('UPD_TENANT_REQUEST: [Upd Tenant] Internal Error: ' + err);
                console.error('UPD_TENANT_REQUEST: [Upd Tenant] Internal Error: ', err);
                showTenantInfoModal(
                    'Update Tenant Error',
                    `Update Tenant: ${tenantName} Failed. Please Contact Application Support`,
                    true,
                    'update'
                );
            },
            onSuccess: (response: GetTenantAdminModelResponse) => {
                showTenantInfoModal(
                    'Update Tenant',
                    `Update Tenant: ${tenantName} Succeeded.`,
                    false,
                    'update'
                );
            },
        }
    );

    const handleCancel = () => {
        reinstateValues();
        props.close();
    };

    const getTenantActionColor = (): string => {
        return props.newTenant ? 'cornflowerblue' : 'darkgray';
    };

    const determineWhichError = () => {
        if ( isCreTenantError ) {
            return currentError + ' ' + ((creTenantError as ManagedApiError).response?.data?.message ?? '');
        }
        else if ( isUpdTenantError ) {
            return currentError + ' ' + ((updTenantError as ManagedApiError).response?.data?.message ?? '');
        }
        else {
            return '';
        }
    };

    return (
        <>
            <ManagedActionModal
                modalState={manageActionModalState}
                onOk={handleManageActionModalOk}
                onCancel={handleManageActionModalOk}
            />
            <Spin spinning={
                            isCreTenantLoading ||
                            isUpdTenantLoading
                           }>
                <div style={{ display: 'table', margin: 'auto', textAlign: 'center', width: '50%' }}>
                {
                    (
                        isCreTenantError ||
                        isUpdTenantError
                    )
                    &&
                    (
                        <ServaAlert
                            className={'mb-2'}
                            message={determineWhichError()}
                            showIcon={true}
                            type='error'
                        ></ServaAlert>
                    )
                }
                </div>
                <Space direction='horizontal' style={{justifyContent: 'center', padding: '8px', width: '100%'}}>
                    <Text style={{height: '100%', width: '100%'}}>
                        <Title style={{backgroundColor: getTenantActionColor(), borderRadius: '10px', color: 'white', height: '100%', padding: '8px', width: '100%'}} level={4}>{props.newTenant ? 'Create New Tenant' : 'Update Existing Tenant'}</Title>
                    </Text>
                </Space>
                <Form
                    form={formCreUpdTenant}
                    initialValues={{
                        creUpdAutoFillTenantGenericName: true,
                        creUpdTenantHideFromDemo: false,
                    }}
                    key='tenantdetails'
                    onFinish={onFinishCreUpdTenant}
                    onFinishFailed={handleTenantFinishFailed}
                >
                    <div style={{ width: '100%', overflowX: 'auto' }}>
                        <Row style={{ padding: '8px', paddingTop: '2px', paddingBottom: '2px', lineHeight: '50%' }}>
                            <Col span={24}>
                                <Form.Item
                                    key='cutenantname'
                                    label='Tenant Name'
                                    labelCol={{ span: 7, }}
                                    name='creUpdTenantName'
                                    rules={[
                                        {
                                            message: 'Tenant Name Required.',
                                            required: true,
                                        },
                                    ]}
                                    style={{marginBottom: '4px'}}
                                >
                                    <Input
                                        value={tenantName}
                                        onChange={handleTenantNameChange}
                                        placeholder='Enter Tenant Name'
                                    ></Input>
                                </Form.Item>
                            </Col>
                        </Row>
                        <Row style={{ padding: '8px', paddingTop: '2px', paddingBottom: '2px', lineHeight: '50%' }}>
                            <Col span={24}>
                                <Form.Item
                                    key='cuconnstring'
                                    label='Conn. String'
                                    labelCol={{ span: 7, }}
                                    name='creUpdConnString'
                                    rules={[
                                        {
                                            message: 'Conn. String Required.',
                                            required: true,
                                        },
                                    ]}
                                    style={{marginBottom: '4px'}}
                                >
                                    <Input
                                        value={connString}
                                        onChange={handleConnStringChange}
                                        placeholder='Enter Conn. String'
                                    ></Input>
                                </Form.Item>
                            </Col>
                        </Row>
                        <Row style={{ padding: '8px', paddingTop: '2px', paddingBottom: '2px', lineHeight: '50%' }}>
                            <Col span={24}>
                                <Form.Item
                                    key='culogoimage'
                                    label='Logo Image'
                                    labelCol={{ span: 7, }}
                                    name='creUpdLogoImage'
                                    style={{marginBottom: '4px'}}
                                >
                                    <Input
                                        value={logoImage}
                                        onChange={handleLogoImageChange}
                                        placeholder='Enter Logo Image'
                                    ></Input>
                                </Form.Item>
                            </Col>
                        </Row>
                        <Row style={{ padding: '8px', paddingTop: '2px', paddingBottom: '2px', lineHeight: '50%' }}>
                            <Col span={19}>
                                <Form.Item
                                    key='cutenantgenericname'
                                    label='Tenant Generic Name'
                                    labelCol={{ span: 8.5, }}
                                    name='creUpdTenantGenericName'
                                    style={{marginBottom: '4px'}}
                                >
                                    <Input
                                        value={tenantGenericName}
                                        onChange={handleTenantGenericNameChange}
                                        placeholder='Enter Tenant Generic Name'
                                    ></Input>
                                </Form.Item>
                            </Col>
                            <Col span={5}>
                                <Form.Item
                                    key='cuautofilltenantgenericname'
                                    name='creUpdAutoFillTenantGenericName'
                                    style={{marginBottom: '4px'}}
                                >
                                    <Checkbox
                                        checked={autoFillTenantGenericName}
                                        onChange={handleAutoFillTenantGenericName}
                                        style={{float: 'right'}}
                                    >
                                        Auto Fill
                                    </Checkbox>
                                </Form.Item>
                            </Col>
                        </Row>
                        <Row style={{ padding: '8px', paddingTop: '2px', paddingBottom: '2px', lineHeight: '50%' }}>
                            <Col span={24} style={{display: 'flex', justifyContent: 'center'}}>
                                <Form.Item
                                    key='cutenanthidefromdemo'
                                    name='creUpdTenantHideFromDemo'
                                    style={{marginBottom: '4px'}}
                                >
                                    <Checkbox
                                        checked={hideFromDemo}
                                        onChange={handleHideFromDemoChange}
                                    >
                                        Hide From Demo
                                    </Checkbox>
                                </Form.Item>
                            </Col>
                        </Row>
                        <Row style={{ padding: '8px', paddingTop: '2px', paddingBottom: '2px', lineHeight: '50%' }}>
                            <Col span={24}>
                                <Form.Item
                                    key='cusqlfirstdayofweek'
                                    label='SQL First DOW'
                                    labelCol={{ span: 7, }}
                                    name='creUpdSQLFirstDayOfWeek'
                                    style={{marginBottom: '4px'}}
                                >
                                    <Input
                                        value={sqlFirstDayOfWeek}
                                        onChange={handleSQLFirstDayOfWeekChange}
                                        placeholder='Enter SQL First DOW'
                                        style={{width: '40%'}}
                                    ></Input>
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                            </Col>
                        </Row>
                        <Row style={{ padding: '8px', paddingTop: '2px', paddingBottom: '2px', lineHeight: '50%' }}>
                            <Col span={24}>
                                <Form.Item
                                    key='culinkeddbname'
                                    label='Linked DB Name'
                                    labelCol={{ span: 7, }}
                                    name='creUpdLinkedDBName'
                                    style={{marginBottom: '4px'}}
                                >
                                    <Input
                                        value={linkedDbName}
                                        onChange={handleLinkedDbNameChange}
                                        placeholder='Enter Linked DB Name'
                                    ></Input>
                                </Form.Item>
                            </Col>
                        </Row>
                        <Row style={{ padding: '8px', paddingTop: '2px', paddingBottom: '2px', lineHeight: '50%' }}>
                            <Col span={24}>
                                <Form.Item
                                    key='cuurlbase'
                                    label='URL Base'
                                    labelCol={{ span: 7, }}
                                    name='creUpdURLBase'
                                    style={{marginBottom: '4px'}}
                                >
                                    <Input
                                        value={urlBase}
                                        onChange={handleURLBaseChange}
                                        placeholder='Enter URL Base'
                                    ></Input>
                                </Form.Item>
                            </Col>
                        </Row>
                        <Row style={{ padding: '8px', paddingTop: '2px', paddingBottom: '2px', lineHeight: '50%' }}>
                            <Col span={24}>
                                <Form.Item
                                    key='cuencryptionkey'
                                    label='Encryption Key'
                                    labelCol={{ span: 7, }}
                                    name='creUpdEncryptionKey'
                                    style={{marginBottom: '4px'}}
                                >
                                    <Input
                                        value={encryptionKey}
                                        onChange={handleEncryptionKeyChange}
                                        placeholder='Enter Encryption Key'
                                    ></Input>
                                </Form.Item>
                            </Col>
                        </Row>
                        <Row style={{ padding: '8px', paddingTop: '2px', paddingBottom: '2px', lineHeight: '50%' }}>
                            <Col span={24}>
                                <Form.Item
                                    key='cuencryptioncert'
                                    label='Encryption Certificate'
                                    labelCol={{ span: 7, }}
                                    name='creUpdEncryptionCert'
                                    style={{marginBottom: '4px'}}
                                >
                                    <Input
                                        value={encryptionCert}
                                        onChange={handleEncryptionCertChange}
                                        placeholder='Enter Encryption Certificate'
                                    ></Input>
                                </Form.Item>
                            </Col>
                        </Row>
                        <hr />
                        <Row>
                            <Col span={16} />
                            <Col span={4}>
                                <Form.Item>
                                    <Button
                                        disabled={submitDisabled}
                                        htmlType='submit'
                                        style={{backgroundColor: getTenantActionColor(), float: 'right'}}
                                        type='primary'
                                    >
                                        Submit
                                    </Button>
                                </Form.Item>
                            </Col>
                            <Col span={4}>
                                <Button onClick={handleCancel}>Cancel</Button>
                            </Col>
                        </Row>
                    </div>
                </Form>
            </Spin>
        </>
    );
};

export default Tenant;
